import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "../../shared-components/Modal";
import Button, { ButtonColorTypes } from "../../shared-components/Button";

interface PersonEditSectionModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => Promise<void>;
  isSaving?: boolean;
  submitDisabled?: boolean;
  saveErrorMessage?: string;
  children: React.ReactNode;
}

/**
 * Shared modal chrome for person section edits (address, employment, education):
 * Modal shell, footer with error message, Cancel, and Submit. Handles save try/catch
 * and inline error display so section modals don't duplicate this logic.
 */
export const PersonEditSectionModal: React.FC<PersonEditSectionModalProps> = ({
  open,
  onClose,
  onSave,
  isSaving = false,
  submitDisabled = false,
  saveErrorMessage = "Failed to save. Please try again.",
  children,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (open) setErrorMessage(null);
  }, [open]);

  const handleSave = useCallback(async () => {
    setErrorMessage(null);
    try {
      await onSave();
      // Parent's onSave closes the modal; do not call onClose() or discard dialog appears.
    } catch {
      setErrorMessage(saveErrorMessage);
    }
  }, [onSave, saveErrorMessage]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="PERSON EDIT"
      titleSize="text-xl"
      hideCloseButton
      width="w-full max-w-2xl"
    >
      {children}
      <div className="flex justify-between items-center gap-3 mt-6 pt-4 border-t border-gray-200">
        <div className="min-h-[2rem] flex items-center">
          {errorMessage && (
            <p className="text-sm text-red-600" role="alert">
              {errorMessage}
            </p>
          )}
        </div>
        <div className="flex gap-3 flex-shrink-0">
          <Button
            text="Cancel"
            colorType={ButtonColorTypes.cancel}
            onClick={onClose}
            disabled={isSaving}
            className="rounded-lg border-gray-300"
          />
          <Button
            text={isSaving ? "Saving..." : "Submit"}
            colorType={ButtonColorTypes.warning}
            onClick={handleSave}
            disabled={isSaving || submitDisabled}
            className="rounded-lg"
          />
        </div>
      </div>
    </Modal>
  );
};
